import { useMemo } from 'react';

import { type QueryKey, useQuery, useQueryClient } from '@tanstack/react-query';

import { useSwallowedMutation } from '../_useSwallowedMutation.js';
import { invalidateMutationKey, resetQueryKeys } from '../_utils.js';
import { useApiClient } from '../useApiClient.js';

import { IMPERSONATION_SESSION_QUERY_KEY } from './constants.js';
import type { ImpersonationSession } from './types.js';
import { useCanImpersonate } from './useCanImpersonate.js';

// JSON:API resource types for the impersonation session and the target user
// relationship. Both follow the backend's PascalCase model-name convention
// (cf. `type: 'User'` in useUsers). The SDK types `type` as an open string, so
// the backend is the only validator — confirm via smoke test.
const IMPERSONATION_SESSION_TYPE = 'ImpersonationSession';
const USER_TYPE = 'User';

interface UseImpersonationSessionsParams {
  enabled?: boolean;
  // Cache key(s) to invalidate after start/exit — consumer-driven, like the
  // other migrated hooks. Callers pass the active-session key plus ME_QUERY_KEY
  // so the banner, header colour and role/scope gating all re-evaluate.
  mutationKey?: QueryKey | QueryKey[];
  resetKey?: QueryKey | QueryKey[];
  onStart?: () => void;
  onExit?: () => void;
  onError?: (error: unknown) => void;
}

interface UseImpersonationSessionsResult {
  session: ImpersonationSession | null;
  isImpersonating: boolean;
  isFetching: boolean;
  isPending: boolean;
  startImpersonation: (targetUserId: string) => Promise<unknown>;
  exitImpersonation: (sessionId: string) => Promise<unknown>;
}

/**
 * Single hook for the `ImpersonationSession` model — owns the active-session
 * query plus the start (create) and exit (end) mutations. Read-only consumers
 * destructure `session` / `isImpersonating`; the users table drives
 * `startImpersonation` and the banner drives `exitImpersonation`. Invalidation
 * is consumer-driven via `mutationKey` (active session + `/users/me`). The
 * backend scopes `end` to the caller's own sessions (404 otherwise).
 */
export const useImpersonationSessions = ({
  enabled = true,
  mutationKey,
  resetKey,
  onStart,
  onExit,
  onError,
}: UseImpersonationSessionsParams = {}): UseImpersonationSessionsResult => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  const canImpersonate = useCanImpersonate();

  const { data, isFetching } = useQuery({
    queryKey: [IMPERSONATION_SESSION_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.users.listUsersImpersonationSessions({});
      return response.data;
    },
    staleTime: Infinity,
    enabled: enabled && canImpersonate,
  });

  const session = useMemo<ImpersonationSession | null>(() => {
    const read = data?.data?.[0];
    if (!read) return null;
    return {
      id: read.id,
      targetUser: {
        id: read.relationships?.targetUser?.data?.id ?? '',
        name: read.attributes?.targetUserName ?? '',
        email: read.attributes?.targetUserEmail ?? '',
      },
    };
  }, [data]);

  const refreshCaches = () => {
    resetQueryKeys(queryClient, resetKey);
    invalidateMutationKey(queryClient, mutationKey);
  };

  const { mutateAsync: startImpersonation, isPending: startPending } = useSwallowedMutation({
    mutationFn: async (targetUserId: string) => {
      const response = await apiClient.users.createUsersImpersonationSessions({
        createUsersImpersonationSessionsRequest: {
          data: {
            type: IMPERSONATION_SESSION_TYPE,
            relationships: {
              targetUser: { data: { type: USER_TYPE, id: targetUserId } },
            },
          },
        },
      });
      return response.data;
    },
    onSuccess: () => {
      onStart?.();
      refreshCaches();
    },
    onError,
  });

  const { mutateAsync: exitImpersonation, isPending: exitPending } = useSwallowedMutation({
    mutationFn: async (sessionId: string) => {
      const response = await apiClient.users.endUsersImpersonationSessionsIdEnd({ id: sessionId });
      return response.data;
    },
    onSuccess: () => {
      onExit?.();
      refreshCaches();
    },
    onError,
  });

  return useMemo(
    () => ({
      session,
      isImpersonating: !!session,
      isFetching,
      isPending: startPending || exitPending,
      startImpersonation,
      exitImpersonation,
    }),
    [session, isFetching, startPending, exitPending, startImpersonation, exitImpersonation]
  );
};
